import { Component, Inject, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { ToastService } from 'src/app/services/toast.service';
import { EMPTY_GUID } from 'src/app/_helpers/guid.helper';
import { trackByIndex } from 'src/app/shared/utils/track-by.util';
import { StepConfig, StepperConfig } from 'src/app/shared/stepper/stepper.model';
import { LabResultImportService } from './lab-result-import.service';
import {
  LAB_IMPORT_UNIT_LABELS,
  LabImportAnalyteMapping,
  LabImportAnalytePreview,
  LabImportColumnMap,
  LabImportOrientation,
  LabImportProposal,
  LabImportSampleAgent,
  LabImportUnitMapping,
  LabImportUnitsEnum,
  LabResultImportDialogData,
  LabResultImportResponse,
  isLabResultImportResponse,
} from './lab-result-import.model';

// Only the string-valued columns are editable via the header dropdowns (excludes fusedColumns: string[]).
type StringColumnKey = Exclude<keyof LabImportColumnMap, 'fusedColumns'>;

interface ColumnMapField {
  key: StringColumnKey;
  label: string;
}

interface AgentOption {
  id: string;
  name: string;
}

@Component({
  standalone: false,
  selector: 'app-lab-result-import-dialog',
  templateUrl: './lab-result-import-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./lab-result-import-dialog.component.scss'],
})
export class LabResultImportDialogComponent implements OnInit {
  // The three step bodies are handed to the shared app-stepper-container as templates.
  @ViewChild('uploadTpl', { static: true }) uploadTpl!: TemplateRef<unknown>;
  @ViewChild('mappingTpl', { static: true }) mappingTpl!: TemplateRef<unknown>;
  @ViewChild('reviewTpl', { static: true }) reviewTpl!: TemplateRef<unknown>;

  // Drives the shared stepper (header + Back/Next + per-step action buttons). Built in ngOnInit
  // once the step templates are available.
  stepperConfig!: StepperConfig;

  // Mirrors the active stepper step (1 upload, 2 mapping-confirm, 3 review). Kept in sync from
  // both the async advance in analyze()/commit() and onStepChanged (Back), so internal state and
  // tests can read a plain numeric step.
  step: 1 | 2 | 3 = 1;

  readonly allowedFileTypes = ['csv', 'xlsx', 'xls'];
  // Defence-in-depth only — file.type is browser-supplied and spoofable; the BE is the authoritative gate.
  readonly allowedMimeTypes = new Set([
    'text/csv',
    'application/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ]);
  readonly maxFileSizeMB = 50;
  // Product default (adjustable, not locked): confidence at/above is "high", below is "low".
  readonly lowConfidenceThreshold = 0.75;

  // DASH-2720: the optional supplemental document (typically the lab's source PDF) is NOT parsed — it is
  // just archived on the requisition after a successful commit (BE DASH-2719). The accepted extensions and
  // MIME types mirror the BE allowlist (AllowedSupplementalExtensions: .pdf/.png/.jpg/.jpeg) so the FE
  // rejects an unsupported file before it hits the wire; the BE remains the authoritative gate.
  readonly allowedSupplementalFileTypes = ['pdf', 'png', 'jpg', 'jpeg'];
  readonly allowedSupplementalMimeTypes = new Set([
    'application/pdf',
    'image/png',
    'image/jpeg',
  ]);

  selectedFile: File | null = null;
  // The supplemental document the user chose (at most one). Sent with commit(); attached only on success.
  selectedSupplementalFile: File | null = null;
  // Inline validation message for the supplemental picker (translation key; the param is always supplied so a
  // non-parameterised key simply ignores it). Null when the current selection is valid / none is chosen.
  supplementalFileError: string | null = null;
  analyzing = false;
  committing = false;

  proposal: LabImportProposal | null = null;
  columnMap: LabImportColumnMap | null = null;
  analytePreviews: LabImportProposal['samples'] = [];
  unitMappings: LabImportUnitMapping[] = [];
  // Pre-computed per-sample agent options (see components.md — no method calls in *ngFor).
  agentOptionsBySample: AgentOption[][] = [];
  applyBlankCorrection = false;

  // AC 13 — detected number-format / dialect. Detected client-side from the CSV (delimiter/decimal
  // are only meaningful for delimited text, so this stays null for xlsx/xls). If the BE proposal
  // ever surfaces these (forward-compat), that value wins over the client-side detection.
  detectedDelimiter: string | null = null;
  detectedDecimalSeparator: string | null = null;

  response: LabResultImportResponse | null = null;

  // Orientation override (DASH-2694 / BE DASH-2693 §3, contract re-verified in DASH-2728). Synced from the BE
  // proposal's nested `orientation` object after each analyze; the user can flip `overrideTransposed` / set
  // `overrideHeaderRow` and re-run analyze. `orientationAcknowledged` gates commit when the BE flags the
  // detection with `requiresConfirmation`.
  overrideTransposed = false;
  // The real header row as it appears in the USER'S FILE, 1-based (spreadsheet row numbering) so "row 5" is
  // the row the user sees — NOT a post-parse offset (DASH-2728). Converted to the BE's 0-based raw-file header
  // index on the wire via overrideHeaderRowIndex. Seeded from the response so a genuine header block prefills
  // to the detected row; defaults to 1 (top of file) when the BE cannot supply one (low-confidence path).
  overrideHeaderRow = 1;
  // DASH-2728 MUST-FIX 1/2: true once the user has actively engaged the orientation override — flipped the
  // transpose toggle, edited the header row, or run a forced re-analyze. This flag SURVIVES applyProposal (a
  // forced re-analyze returns requiresConfirmation=false, so that alone cannot carry the intent across the
  // round-trip). While it is set: the header-row control stays
  // visible (AC9 recovery path is not DOM-removed after a forced re-analyze) and BOTH transposed + the header
  // index are echoed on commit (AC8 — otherwise the confirmed layout is dropped and the BE rebuilds at the
  // AI's header row → every column resolves -1 → HTTP 400). Reset only by a fresh (non-reanalyze) analyze.
  orientationOverrideActive = false;
  orientationAcknowledged = false;

  // Manual orientation override (transpose toggle / data-start-row / re-analyze) is now enabled: the paired
  // BE change (DASH-2703) has merged, so LabImportAnalyzeRequestModel binds `Transposed`/`DataStartRowIndex`
  // and AnalyzeAsync FORCES the chosen orientation on re-analyze (ApplyForcedOrientation → AutoApplied=false,
  // RequiresConfirmation=false) instead of silently dropping the fields and re-running auto-detection. The
  // reanalyze() wiring was retained behind this flag while the FE controls were gated off (DASH-2694,
  // commit 3b42ad319), so un-gating is this one-line flip. Kept as a flag (not deleted) so the panel can be
  // re-gated cheaply if the BE contract regresses.
  readonly orientationOverrideSupported = true;

  // Pre-computed orientation view state, updated in syncOrientationControls() after each proposal.
  // Bound as plain property reads in the template (no getters/method calls per components.md).
  orientationAvailable = false;
  isHeaderBlockOrientation = false;
  // The BE's authoritative "user must confirm/override before commit" flag (proposal.orientation.requiresConfirmation).
  orientationRequiresConfirmation = false;
  orientationLabel = '';
  // AC9: whether the data-start (header row) control is shown. Un-gated from headerBlock-only to EVERY
  // orientation whenever the BE flags the layout for confirmation — otherwise a banner-block file
  // misclassified as columnar/ambiguous below the confidence threshold has NO recovery path in the UI.
  showDataStartControl = false;
  // Per-section confidence (DASH-2728): the ORIENTATION confidence, surfaced independently of the
  // column-map/unit confidence so a reviewer sees which dimension is uncertain (never a blended value).
  orientationConfidencePercent = 0;
  orientationLowConfidence = false;

  // DASH-3159: the structure the BE ACTUALLY APPLIED when it built the previewed table, echoed on EVERY commit
  // so the BE rebuilds the identical table deterministically (not only when the user engaged an override).
  // Seeded from the proposal's orientation on every proposal — INCLUDING a re-analyze, since these describe
  // the latest build. Kept DISTINCT from the editable override controls: `overrideHeaderRow` prefills the AI's
  // PROPOSED header for correction, whereas `appliedHeaderRowIndex` is what was built (header-0 on the
  // low-confidence path, so a pure acknowledgement reproduces the previewed build rather than forcing the
  // untrusted AI proposal). `appliedFooterRowIndices` supersedes DASH-2728 MUST-FIX 4: the previously-withheld
  // footer set is now the APPLIED set (empty on the low-confidence path), so echoing it is safe and exact.
  appliedTransposed = false;
  appliedHeaderRowIndex = 0;
  // DASH-3159: the applied FIRST-DATA-row index (echoes LabImportOrientation.dataStartRowIndex), distinct from
  // the header row above — they differ when interstitial metadata sits between the header and the first data
  // row. Echoed on the applied-structure commit as `dataStartRowIndex` (which on that path means first-data-row,
  // not the header). 0 on the low-confidence path (the BE built header-0 and returns a 0 placeholder).
  appliedDataStartRowIndex = 0;
  appliedFooterRowIndices: number[] = [];

  // DASH-2728 MUST-FIX 3: proposal.warnings is a MIXED list — genuine excluded DATA rows ("Row N: Excluded
  // as …") alongside non-row analysis notes ("No sample-number column could be identified", "The file layout
  // could not be confidently classified …"). Pre-computed (no method calls in templates — components.md) into
  // two buckets so a "confirm the orientation" instruction is never shown to a compliance reviewer under a
  // "these rows will NOT be imported" heading. Populated in applyProposal(); a leading "Row N:" is the split.
  excludedRowWarnings: string[] = [];
  analysisWarnings: string[] = [];

  // Full-row preview pagination (DASH-2684). The BE (DASH-2682) now returns every parsed row rather
  // than a 15-row cap, so a reviewer can verify the whole file before committing. Paginate client-side
  // so a large batch renders a bounded number of DOM rows at a time instead of thousands at once.
  readonly previewPageSize = 25;
  previewPageIndex = 0;

  // Labels use LEAP terminology (DASH-2694), not lab jargon. The mass trio (Mass / Mass Units / Mass
  // detection-flag) rides the same LabImportColumnMap commit payload; the AI pre-fills Mass vs
  // Concentration from the units and the user can still override each via the dropdown.
  readonly columnMapFields: ColumnMapField[] = [
    { key: 'sampleNumberColumn', label: 'Sample Number' },
    { key: 'contaminantColumn', label: 'Agent' },
    { key: 'valueColumn', label: 'Concentration' },
    { key: 'unitsColumn', label: 'Concentration Units' },
    { key: 'censorTypeColumn', label: 'Detection-limit flag' },
    { key: 'totalColumn', label: 'Mass' },
    { key: 'totalUnitsColumn', label: 'Mass Units' },
    { key: 'totalCensorTypeColumn', label: 'Mass detection-flag' },
    { key: 'methodColumn', label: 'Method' },
    { key: 'mediaColumn', label: 'Media' },
    { key: 'sampleDateColumn', label: 'Sample Date' },
    { key: 'dateReceivedColumn', label: 'Date Received' },
    { key: 'labSampleNumberColumn', label: 'Lab Sample Number' },
  ];

  // Label shown above a raw source header for a column the AI/user fused into a single
  // censor+value+unit column (columnMap.fusedColumns), distinct from a standard column-map field label.
  readonly fusedColumnLabel = 'Fused (Censor/Value/Unit)';

  // The unmapped-analyte sentinel. The unmapped <mat-option> binds this so the dropdown puts a real
  // empty-GUID string on mappedAgentId — the value the BE returns for an unresolved analyte and can
  // bind into a Guid — rather than the numeric 0 that failed to deserialize (DASH-3158).
  readonly emptyGuid = EMPTY_GUID;

  readonly unitOptions: { value: LabImportUnitsEnum; label: string }[] =
    Object.values(LabImportUnitsEnum)
      .filter((v): v is LabImportUnitsEnum => typeof v === 'number')
      .map(value => ({ value, label: LAB_IMPORT_UNIT_LABELS[value] }));

  constructor(
    private dialogRef: MatDialogRef<LabResultImportDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: LabResultImportDialogData,
    private importService: LabResultImportService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    // Reuse the shared stepper (UI.md §2 — do not hand-roll the step header). Its onNext(): Promise<boolean>
    // hook gates the forward move on the async analyze/commit calls; canProceed additionally blocks a
    // header click from skipping a step before its server round-trip has produced data. linear keeps the
    // 3-step flow strictly Upload → Review Mapping → Review.
    this.stepperConfig = {
      orientation: 'horizontal',
      linear: true,
      steps: [
        {
          id: 'upload',
          label: 'Upload',
          template: this.uploadTpl,
          nextLabel: 'Analyze',
          canProceed: () => !!this.proposal,
          onNext: () => this.analyze(),
          buttons: [{
            label: 'Cancel',
            class: 'transparent-btn bordered-btn px-5',
            action: () => this.cancel(),
            disabled: () => this.analyzing,
          }],
        },
        {
          id: 'mapping',
          label: 'Review Mapping',
          template: this.mappingTpl,
          nextLabel: 'Confirm',
          canProceed: () => !!this.response,
          onNext: () => this.commit(),
          buttons: [],
        },
        {
          id: 'review',
          label: 'Review',
          template: this.reviewTpl,
          buttons: [{
            label: 'Done',
            class: 'custom-main-btn px-5',
            action: () => this.close(),
          }],
        },
      ],
    };
  }

  // In-flight status for the blocking loader (DASH-2684). Analyze runs two sequential AI/LLM calls
  // server-side (column mapping + analyte mapping) and can take many seconds; commit re-parses and
  // persists. A bare spinner reads as "stuck", so surface what is happening. Returns '' when idle so
  // the template can gate the overlay on a truthy message.
  get loaderMessage(): string {
    if (this.analyzing) return 'Analyzing file — mapping columns and analytes…';
    if (this.committing) return 'Importing results…';
    return '';
  }

  onStepChanged(step: StepConfig): void {
    const stepByStepId: Record<string, 1 | 2 | 3> = { upload: 1, mapping: 2, review: 3 };
    this.step = stepByStepId[step.id] ?? this.step;
  }

  // ---- Step 1: upload ----

  onFileSelected(event: Event | FileList): void {
    const files = event instanceof FileList ? event : (event.target as HTMLInputElement)?.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!this.isFileValid(file)) return;
    this.selectedFile = file;
  }

  isFileValid(file: File): boolean {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !this.allowedFileTypes.includes(ext)) {
      this.toastService.displayErrorMessage('Invalid file type: {{ext}}', true, { ext: ext ?? '' });
      return false;
    }
    // Defence-in-depth: reject an obvious content-type mismatch (empty file.type is allowed — some
    // browsers/OSes don't set it for .csv). Not a replacement for BE validation, which is authoritative.
    if (file.type && !this.allowedMimeTypes.has(file.type)) {
      this.toastService.displayErrorMessage('Invalid file type: {{ext}}', true, { ext });
      return false;
    }
    if (file.size > this.maxFileSizeMB * 1024 * 1024) {
      this.toastService.displayErrorMessage('File size exceeds {{maxFileSizeMB}}MB limit', true, { maxFileSizeMB: this.maxFileSizeMB });
      return false;
    }
    return true;
  }

  removeFile(): void {
    this.selectedFile = null;
  }

  // ---- Supplemental document (DASH-2720) ----
  // The supplemental file is optional, unparsed, and attached to the requisition only after a successful
  // commit (BE DASH-2719). Validation mirrors the BE allowlist and shows an INLINE error (not a toast) so the
  // reason sits next to the picker. A rejected pick clears any previous valid selection so a bad file can
  // never be silently committed.

  onSupplementalFileSelected(event: Event | FileList): void {
    const files = event instanceof FileList ? event : (event.target as HTMLInputElement)?.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (!this.isSupplementalFileValid(file)) {
      this.selectedSupplementalFile = null;
      return;
    }
    this.supplementalFileError = null;
    this.selectedSupplementalFile = file;
  }

  // Returns true and clears the inline error when the file is an accepted type and within the size limit;
  // otherwise sets `supplementalFileError` to a translation key and returns false.
  isSupplementalFileValid(file: File): boolean {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!ext || !this.allowedSupplementalFileTypes.includes(ext)) {
      this.supplementalFileError = 'The supplemental document must be a PDF or image (.pdf, .png, .jpg, .jpeg).';
      return false;
    }
    // Defence-in-depth: reject an obvious content-type mismatch. Empty file.type is tolerated (some
    // browsers/OSes omit it); the BE remains the authoritative gate.
    if (file.type && !this.allowedSupplementalMimeTypes.has(file.type)) {
      this.supplementalFileError = 'The supplemental document must be a PDF or image (.pdf, .png, .jpg, .jpeg).';
      return false;
    }
    if (file.size > this.maxFileSizeMB * 1024 * 1024) {
      this.supplementalFileError = 'The supplemental document exceeds the {{maxFileSizeMB}}MB limit.';
      return false;
    }
    this.supplementalFileError = null;
    return true;
  }

  removeSupplementalFile(): void {
    this.selectedSupplementalFile = null;
    this.supplementalFileError = null;
  }

  // Returns Promise<boolean> for the stepper's onNext gate: resolves true (advance to mapping) only
  // once the proposal has loaded, false on a guard hit or a failed analyze.
  analyze(): Promise<boolean> {
    if (!this.selectedFile || this.analyzing) return Promise.resolve(false);
    this.analyzing = true;
    const file = this.selectedFile;
    this.detectDialect(file);
    return new Promise<boolean>(resolve => {
      this.importService.analyze(this.data.labRequisitionId, file).subscribe({
        next: proposal => {
          this.applyProposal(proposal);
          this.step = 2;
          this.analyzing = false;
          resolve(true);
        },
        error: (err: HttpErrorResponse) => {
          this.analyzing = false;
          this.toastService.showError(err, 'Could not analyze the file');
          resolve(false);
        },
      });
    });
  }

  // Matches a warning that names a specific excluded DATA row ("Row 4: Excluded as …"), so those can be
  // partitioned from non-row analysis notes (MUST-FIX 3). Anchored + case-insensitive; the BE emits "Row N:".
  private static readonly EXCLUDED_ROW_WARNING = /^\s*Row\s+\d+\s*:/i;

  // Captures the leading "Row {index}:" prefix so its number can be re-based (see rebaseExcludedRowWarning).
  private static readonly EXCLUDED_ROW_PREFIX = /^(\s*Row\s+)(\d+)(\s*:)/i;

  // The BE emits excluded-row warnings 0-based ("Row 0: Excluded as a leading banner…"), but the whole
  // orientation UI is 1-based — the "Header row in file" control right beside this callout documents "row 1 =
  // the first row". Re-base the leading row number to 1-based so both numbers the reviewer sees refer to the
  // same physical row. Only the "Row N:" prefix is rewritten; the rest of the message is left untouched.
  private static rebaseExcludedRowWarning(warning: string): string {
    return warning.replace(
      LabResultImportDialogComponent.EXCLUDED_ROW_PREFIX,
      (_match, prefix: string, num: string, suffix: string) => `${prefix}${Number(num) + 1}${suffix}`,
    );
  }

  // Applies an analyze proposal to the editable component state. Shared by the initial analyze() and the
  // orientation reanalyze() so both paths derive identical pre-computed mappings/options. `fromReanalyze`
  // is true only for a user-forced re-analyze: the orientation override state (transpose toggle, header row,
  // override-active flag) is then PRESERVED rather than re-seeded from the AI's post-force detection (which
  // reports autoApplied=false / requiresConfirmation=false and would otherwise flip the user's choices back).
  private applyProposal(proposal: LabImportProposal, fromReanalyze = false): void {
    this.proposal = proposal;
    // DASH-3159: on a RE-ANALYZE the user's confirmed column map was just submitted and the BE derived the
    // returned analyte/unit previews from it, so keep the user's edits rather than overwriting `columnMap`
    // from the proposal's echoed columns — otherwise the correction the user just submitted (e.g. the fixed
    // Sample Number column) is discarded and the dropdown snaps back. A fresh analyze (or a defensive
    // never-seeded map) still seeds the editable copy from the proposal.
    if (!fromReanalyze || !this.columnMap) {
      this.columnMap = { ...proposal.columns };
    }
    // MUST-FIX 3: split the mixed warnings list into genuine excluded rows vs. general analysis notes.
    const warnings = proposal.warnings ?? [];
    this.excludedRowWarnings = warnings
      .filter(w => LabResultImportDialogComponent.EXCLUDED_ROW_WARNING.test(w))
      .map(w => LabResultImportDialogComponent.rebaseExcludedRowWarning(w));
    this.analysisWarnings = warnings.filter(w => !LabResultImportDialogComponent.EXCLUDED_ROW_WARNING.test(w));
    // Pre-compute lowConfidence on every mapping item so the template binds a field, not a method.
    this.analytePreviews = (proposal.samples ?? []).map(s => ({
      ...s,
      analytes: s.analytes.map(a => ({ ...a, lowConfidence: a.confidence < this.lowConfidenceThreshold })),
    }));
    this.unitMappings = (proposal.units ?? []).map(u => ({
      ...u,
      lowConfidence: u.confidence < this.lowConfidenceThreshold,
    }));
    // Pre-compute the per-sample agent override options from the sample's FULL expected-agent set
    // — not just the AI-proposed analytes — so a reviewer can map to any agent the sample
    // was sampled for, including when an analyte (or all of them) is unmapped.
    this.agentOptionsBySample = this.analytePreviews.map(sample => this.buildAgentOptions(sample));
    this.applyBlankCorrection = proposal.applyBlankCorrection;
    this.previewPageIndex = 0;
    this.syncOrientationControls(proposal, fromReanalyze);
  }

  // Seed the override controls from the freshly detected orientation. A new proposal always resets the
  // acknowledgement so a still-low-confidence result must be re-confirmed before commit. On a user-forced
  // re-analyze (`fromReanalyze`) the user's override choices are preserved, not re-seeded (see applyProposal).
  private syncOrientationControls(proposal: LabImportProposal, fromReanalyze = false): void {
    // BE DASH-2693 §3 serializes orientation as a nested object; read its fields, don't treat it as a string.
    const orientation = proposal.orientation;
    const detected = orientation?.detectedOrientation;

    if (!fromReanalyze) {
      // A fresh analyze re-seeds the override controls from the detection and clears any prior override intent.
      this.orientationOverrideActive = false;
      // DASH-2728 MUST-FIX 2: seed the transpose toggle from what the BE actually APPLIED (`autoApplied`), NOT
      // from what it merely detected. On the low-confidence path the BE reports detectedOrientation='transposed'
      // but applies NO transpose (autoApplied=false; it builds header-0 and requires confirmation). Seeding the
      // toggle ON there would echo transposed=true on commit and transpose a table the user never previewed.
      // Only a genuinely auto-applied transpose (the preview the user sees) seeds the toggle ON.
      this.overrideTransposed = detected === 'transposed' && (orientation?.autoApplied ?? false);
      // Prefill the header-row control from the AI's PROPOSED header index. `headerRowIndex` is 0-based against
      // the raw grid — the SAME basis the request override uses — so the 1-based user-facing row is
      // headerRowIndex + 1. It is populated on EVERY build path, including the low-confidence one (where the
      // response `dataStartRowIndex` is a 0 placeholder), so a low-confidence file now prefills to the AI's
      // proposed row rather than row 1 — the case where the user needs the most help.
      // Deploy-order safety: a BE that predates `headerRowIndex` omits it; fall back to the response's 0-based
      // first-data-row `dataStartRowIndex`, which numerically equals the 1-based header row on the
      // high-confidence path — preserving the prior behaviour instead of collapsing to row 1.
      this.overrideHeaderRow = orientation?.headerRowIndex != null
        ? Math.max(1, orientation.headerRowIndex + 1)
        : Math.max(1, orientation?.dataStartRowIndex ?? 1);
    }
    this.orientationAcknowledged = false;

    // Deploy-order safety: a BE predating §3 omits `orientation` entirely (detected == null) → panel hidden.
    // A §3 BE always sends a populated nested object with a known detectedOrientation.
    this.orientationAvailable = detected != null;
    this.isHeaderBlockOrientation = detected === 'headerBlock';
    // Trust the BE's authoritative confirmation flag rather than recomputing a client-side confidence gate.
    this.orientationRequiresConfirmation = orientation?.requiresConfirmation ?? false;
    // AC9: show the header-row control for a detected header block, for any layout the BE flagged for
    // confirmation (columnar/ambiguous below threshold), AND — critically — once the user has engaged an
    // override. A forced re-analyze returns requiresConfirmation=false with the AI's raw label, so without
    // orientationOverrideActive the control would be DOM-removed the moment the user corrected the layout,
    // stranding them with no way to re-send the index (MUST-FIX 1 downstream / AC9).
    this.showDataStartControl = this.isHeaderBlockOrientation
      || this.orientationRequiresConfirmation
      || this.orientationOverrideActive;
    // Per-section confidence — orientation confidence stands on its own (a forced override reports 1.0).
    this.orientationConfidencePercent = Math.round((orientation?.confidence ?? 0) * 100);
    // The orientation chip's low-confidence tag is driven by the BE's authoritative `requiresConfirmation`
    // flag — NOT a second FE copy of the confidence threshold. The FE `lowConfidenceThreshold` (0.75, used for
    // the per-analyte/unit chips) disagreed with the BE's auto-apply threshold (0.8), so a 0.78 file rendered
    // the "orientation is uncertain" banner while the chip beside it read "78%" with no low-confidence tag.
    // Reading the same BE flag the blocking gate uses keeps the chip and the banner in agreement.
    this.orientationLowConfidence = this.orientationRequiresConfirmation;
    const labels: Record<LabImportOrientation, string> = {
      columnar: 'Columnar',
      transposed: 'Transposed',
      headerBlock: 'Header block',
      ambiguous: 'Ambiguous',
    };
    this.orientationLabel = detected ? labels[detected] : '';

    // DASH-3159: capture the APPLIED structure from the latest proposal (both fresh analyze and re-analyze —
    // these describe what the BE just built). `appliedTransposed`/`footerRowIndices` come straight from the BE
    // when present; the transpose flag falls back to the same derivation the toggle seeds from
    // (auto-applied transpose only). The applied header index is 0 on the low-confidence path (the BE built
    // header-0 and discarded its structural indices) and the proposed header index otherwise — so a pure
    // acknowledgement echoes header-0 and reproduces the previewed build instead of forcing the AI's proposal.
    this.appliedTransposed = orientation?.transposed
      ?? (detected === 'transposed' && (orientation?.autoApplied ?? false));
    this.appliedFooterRowIndices = orientation?.footerRowIndices ?? [];
    // On the low-confidence path (requiresConfirmation) the BE built header-0 and discarded its structural
    // indices (both header and first-data-row come back as 0 placeholders), so echo header-0 / data-start-0.
    // On the confident path echo the REAL header index and the FIRST-DATA-row index separately — they differ
    // when interstitial rows sit between the header and the data, and the BE's applied-structure path consumes
    // both (HeaderRowIndex gates the deterministic rebuild; DataStartRowIndex is the first data row).
    this.appliedHeaderRowIndex = (orientation?.requiresConfirmation ?? false)
      ? 0
      : (orientation?.headerRowIndex ?? 0);
    this.appliedDataStartRowIndex = (orientation?.requiresConfirmation ?? false)
      ? 0
      : (orientation?.dataStartRowIndex ?? 0);
  }

  // ---- Step 2: mapping-confirm ----

  get headers(): string[] {
    return this.proposal?.headers ?? [];
  }

  get blankCorrectionDisabled(): boolean {
    return (this.proposal?.detectedBlankCount ?? 0) === 0;
  }

  // Override dropdown for an analyte is constrained to the sample's FULL expected-agent set — every agent
  // the sample was sampled for, not just the AI-proposed ones. This is what makes the control usable when
  // an analyte is unmapped or all analytes are unmapped. Called once per sample after analyze() to populate
  // agentOptionsBySample — never from the template.
  // Deploy-order safety: if the BE has not yet started supplying expectedAgents (separate repos/pipelines,
  // non-atomic deploy), fall back to the agents the AI proposed for this sample's analytes — the prior
  // behaviour — so the dropdown never degrades to empty.
  private buildAgentOptions(sample: LabImportAnalytePreview): AgentOption[] {
    const source: LabImportSampleAgent[] = sample.expectedAgents?.length
      ? sample.expectedAgents
      : sample.analytes
          .filter(a => a.mappedAgentId && a.mappedAgentId !== EMPTY_GUID)
          .map(a => ({ agentId: a.mappedAgentId, name: a.mappedAgentName }));
    const seen = new Map<string, string>();
    for (const a of source) {
      if (a.agentId && !seen.has(a.agentId)) {
        seen.set(a.agentId, a.name);
      }
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }

  onAgentOverride(analyte: LabImportAnalyteMapping, agentId: string, sampleIndex: number): void {
    analyte.mappedAgentId = agentId;
    // Empty-GUID is the "unmapped" sentinel (DASH-3158) — treat it like a cleared selection.
    if (!agentId || agentId === EMPTY_GUID) {
      analyte.mappedAgentName = '';
      return;
    }
    const match = (this.agentOptionsBySample[sampleIndex] ?? []).find(o => o.id === agentId);
    analyte.mappedAgentName = match?.name ?? analyte.mappedAgentName;
    analyte.basis = 'manual';
  }

  onUnitOverride(unit: LabImportUnitMapping, mappedUnit: LabImportUnitsEnum | null): void {
    unit.mappedUnit = mappedUnit;
    unit.basis = 'manual';
  }

  unitLabel(mappedUnit: LabImportUnitsEnum | null): string {
    return mappedUnit == null ? '' : LAB_IMPORT_UNIT_LABELS[mappedUnit];
  }

  // Unrecognized units are blocking review errors.
  get unmappedUnits(): LabImportUnitMapping[] {
    return this.unitMappings.filter(u => u.mappedUnit == null);
  }

  get hasBlockingErrors(): boolean {
    return (this.proposal?.sampleMatchErrors?.length ?? 0) > 0
      || this.unmappedUnits.length > 0
      // A layout the BE flagged for confirmation must be explicitly acknowledged before commit.
      || (this.orientationRequiresConfirmation && !this.orientationAcknowledged);
  }

  // ---- Step 2: sheet-orientation override ----
  // Deploy-order safety: the orientation panel (guarded by `orientationAvailable`) is shown only when the
  // BE proposal surfaces `orientation`. A BE that predates the paired change omits it, so the whole panel
  // is hidden and behaviour is unchanged. The view-state fields are pre-computed in
  // syncOrientationControls() — see the field declarations above.

  // Converts the user-facing 1-based header row to the BE's 0-based RAW-FILE header index, clamped to
  // [0, 10000]. DASH-2728: the 10000 bound still holds against raw row counts — a header/banner block sits at
  // the top of the file, so even a 50 MB sheet with far more raw rows than parsed rows never needs a header
  // index that large; the bound guards a fat-fingered value, not a legitimate one.
  get overrideHeaderRowIndex(): number {
    return Math.max(0, Math.min(Math.floor((this.overrideHeaderRow || 1) - 1), 10000));
  }

  // The header-row input changed — mark the override active (so the control stays visible and the index is
  // echoed on commit across a re-analyze) and re-arm the confirmation gate.
  onDataStartRowChanged(value: number): void {
    this.overrideHeaderRow = value;
    this.orientationOverrideActive = true;
    this.orientationAcknowledged = false;
  }

  // The transpose toggle was flipped by the user — mark the override active (the chosen transpose must now be
  // echoed on commit rather than seeded away) and re-arm the confirmation gate.
  onTransposeToggled(): void {
    this.orientationOverrideActive = true;
    this.orientationAcknowledged = false;
  }

  // Re-run analyze with the user's chosen orientation AND confirmed column map (DASH-3159). Not a stepper gate
  // (the user stays on the mapping step), so it manages `analyzing` itself and re-applies the returned proposal
  // in place. Resubmitting the column map is what lets a corrected Sample Number / Agent column drive fresh
  // matched samples, analyte previews and unit mappings — the user's edit is actually applied, not discarded.
  reanalyze(): void {
    if (!this.selectedFile || this.analyzing) return;
    // A re-analyze IS an override — mark it active before the round-trip so applyProposal preserves the user's
    // choices and the header-row control is not DOM-removed when the forced proposal returns requiresConfirmation=false.
    this.orientationOverrideActive = true;
    this.analyzing = true;
    this.importService.analyze(this.data.labRequisitionId, this.selectedFile, {
      transposed: this.overrideTransposed,
      // AC9: send the header-row override whenever the control is shown (header block OR a layout flagged for
      // confirmation), as the 0-based raw-file HEADER index — never the response's first-data-row value.
      dataStartRowIndex: this.showDataStartControl ? this.overrideHeaderRowIndex : undefined,
    }, this.columnMap ?? undefined).subscribe({
      next: proposal => {
        this.applyProposal(proposal, true);
        this.analyzing = false;
      },
      error: (err: HttpErrorResponse) => {
        this.analyzing = false;
        this.toastService.showError(err, 'Could not re-analyze the file');
      },
    });
  }

  // BE-provided dialect (forward-compat) wins over the client-side detection when present.
  get effectiveDelimiter(): string | null {
    return this.proposal?.delimiter ?? this.detectedDelimiter;
  }

  get effectiveDecimalSeparator(): string | null {
    return this.proposal?.decimalSeparator ?? this.detectedDecimalSeparator;
  }

  get showDialectBanner(): boolean {
    return !!this.effectiveDelimiter || !!this.effectiveDecimalSeparator;
  }

  // A tab is invisible, so render it as a translated word; every other delimiter/separator is a
  // single printable glyph shown verbatim (avoids locale-ambiguous descriptor translations).
  delimiterDisplay(delimiter: string | null): string {
    return delimiter === '\t' ? 'Tab' : (delimiter ?? '');
  }

  // Bound by the template so interpolation reads a value, not a method call
  // (rule: no method calls in templates). Cheap ternary derived from the effectiveDelimiter getter.
  get delimiterDisplayValue(): string {
    return this.delimiterDisplay(this.effectiveDelimiter);
  }

  // Detect the CSV dialect client-side from the raw file text. Only CSV is delimited text; xlsx/xls
  // are binary spreadsheets with no delimiter/decimal-separator concept, so detection is skipped.
  private detectDialect(file: File): void {
    this.detectedDelimiter = null;
    this.detectedDecimalSeparator = null;
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (ext !== 'csv') return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === 'string' ? reader.result : '';
      const dialect = this.parseCsvDialect(text);
      this.detectedDelimiter = dialect.delimiter;
      this.detectedDecimalSeparator = dialect.decimalSeparator;
    };
    // Sniff only the first 64KB — enough for the header + sample rows, avoids loading huge files.
    reader.readAsText(file.slice(0, 65536));
  }

  private parseCsvDialect(text: string): { delimiter: string; decimalSeparator: string } {
    const lines = text.split(/\r\n|\r|\n/).filter(l => l.trim().length > 0);
    const header = lines[0] ?? '';
    // Pick the candidate delimiter that appears most often in the header row.
    let delimiter = ',';
    let bestCount = 0;
    for (const candidate of [',', ';', '\t', '|']) {
      const count = header.split(candidate).length - 1;
      if (count > bestCount) {
        bestCount = count;
        delimiter = candidate;
      }
    }
    // A comma delimiter rules comma out as a decimal separator. Otherwise infer from the body:
    // whichever of "digit,digit" / "digit.digit" appears more often across the sampled rows.
    let decimalSeparator = '.';
    if (delimiter !== ',') {
      const body = lines.slice(1, 50).join('\n');
      const commaDecimals = (body.match(/\d,\d/g) ?? []).length;
      const dotDecimals = (body.match(/\d\.\d/g) ?? []).length;
      if (commaDecimals > dotDecimals) decimalSeparator = ',';
    }
    return { delimiter, decimalSeparator };
  }

  // DASH-3159 (supersedes DASH-2728 AC8): echo the APPLIED structure on EVERY commit so the BE rebuilds the
  // SAME table the user reviewed, deterministically, instead of re-running the AI structure pass (which could
  // re-derive a different layout and diverge from the preview). This now fires whether or not the user engaged
  // an override, because the BE surfaces the APPLIED footer set / transpose (LabImportOrientationInfo), so the
  // fields we echo describe what was actually built — not the AI's discarded proposal that MUST-FIX 4 guarded
  // against. Rationale by path:
  //  - Deploy-order safety: echo nothing when the BE did not surface orientation (a BE predating DASH-2727
  //    ignores these fields and re-detects — the prior behaviour).
  //  - Override engaged (toggle / header edit / forced re-analyze): echo the user's chosen transpose + header
  //    index. orientationOverrideActive persists the intent across a forced re-analyze (which returns
  //    requiresConfirmation=false), so the confirmed header index is not dropped and the BE reproduces the
  //    forced build rather than rebuilding at the AI's header row (→ HTTP 400).
  //  - No override (clean file or a PURE low-confidence acknowledgement): echo the FULL APPLIED structure the
  //    BE built — real header index + first-data-row index + transpose + footer set — so the BE takes its
  //    deterministic applied-structure path (gated on HeaderRowIndex) and rebuilds the identical table with NO
  //    AI structure call, instead of re-detecting from scratch and silently ignoring the echoed footer set.
  private buildCommitOrientation(): { transposed?: boolean; dataStartRowIndex?: number; headerRowIndex?: number; footerRowIndices?: number[] } {
    if (!this.orientationAvailable) return {};
    if (this.orientationOverrideActive) {
      // Legacy override path (DASH-2728): the user forced a layout that supersedes the previously-applied
      // structure, so send only the forced transpose + REAL-header-row override (as dataStartRowIndex, the
      // legacy meaning) with NO headerRowIndex — the BE re-detects the footer set against the forced layout.
      return { transposed: this.overrideTransposed, dataStartRowIndex: this.overrideHeaderRowIndex };
    }
    // Applied-structure path (DASH-3159/3157): echo the exact structure the BE built. Sending headerRowIndex is
    // what triggers the BE's deterministic rebuild; dataStartRowIndex here is the FIRST-DATA-row index (≠ header
    // row), and footerRowIndices reproduces the excluded-row set from the preview byte-for-byte.
    return {
      transposed: this.appliedTransposed,
      headerRowIndex: this.appliedHeaderRowIndex,
      dataStartRowIndex: this.appliedDataStartRowIndex,
      footerRowIndices: this.appliedFooterRowIndices,
    };
  }

  // Returns Promise<boolean> for the stepper's onNext gate: resolves true (advance to review) on a
  // 200/207 success or a 400-with-body (nothing imported, shown in the review banner), false otherwise.
  commit(): Promise<boolean> {
    if (this.committing) return Promise.resolve(false);
    // The shared stepper keeps Back reachable from the Review step; once a batch has been successfully
    // ingested, re-confirming must not re-send the file. A fully-failed 400 (importFailedEntirely)
    // imported nothing, so a retry is still allowed.
    if (this.response && !this.importFailedEntirely) return Promise.resolve(true);
    if (!this.selectedFile || !this.columnMap || this.hasBlockingErrors) return Promise.resolve(false);
    this.committing = true;
    const file = this.selectedFile;
    const columns = this.columnMap;
    // DASH-2713: stamp each analyte with its owning sample number so a per-sample override round-trips.
    // Previously this flattened every sample's analytes into one label->agent list, and the BE re-keyed by
    // label alone (last-wins) — collapsing a shared label to ONE agent file-wide and rejecting the rest.
    const analytes: LabImportAnalyteMapping[] = this.analytePreviews.flatMap(s =>
      s.analytes.map(a => ({ ...a, sampleNumber: s.sampleNumber })),
    );
    const orientationOverride = this.buildCommitOrientation();
    return new Promise<boolean>(resolve => {
      this.importService.commit({
        labRequisitionId: this.data.labRequisitionId,
        file,
        applyBlankCorrection: this.applyBlankCorrection,
        columns,
        analytes,
        units: this.unitMappings,
        ...orientationOverride,
        // DASH-2720: the supplemental document (if any) rides the same commit request. The BE archives it on
        // the requisition ONLY after a successful ingest, so a cancel or a fully-failed commit attaches nothing.
        supplementalFile: this.selectedSupplementalFile,
      }).subscribe({
        next: response => {
          // 200 (clean) or 207 (partial) — both land here.
          this.response = response;
          this.step = 3;
          this.committing = false;
          resolve(true);
        },
        error: (err: HttpErrorResponse) => {
          this.committing = false;
          // 400 with a full response body => nothing imported: show the review with the banner.
          // Validate the minimum shape first so an unexpected proxy/WAF 400 isn't rendered as a response.
          if (err.status === 400 && isLabResultImportResponse(err.error)) {
            this.response = err.error;
            this.step = 3;
            resolve(true);
            return;
          }
          this.toastService.showError(err, 'Could not import results');
          resolve(false);
        },
      });
    });
  }

  // ---- Step 2: full-row preview pagination (DASH-2684) ----

  get previewTotal(): number {
    return this.proposal?.preview.length ?? 0;
  }

  // The rows for the current page only — bounds the rendered DOM to previewPageSize regardless of
  // how many rows the BE parsed. Bound by the template (property access, not a method call).
  get pagedPreviewRows(): string[][] {
    const start = this.previewPageIndex * this.previewPageSize;
    return this.proposal?.preview.slice(start, start + this.previewPageSize) ?? [];
  }

  // Raw source header -> the LEAP field label it is currently mapped to in `columnMap`,
  // so the Data Preview can render the mapped field above each raw header. Reads the SAME columnMap the
  // Column Mapping dropdowns edit, so an AI proposal or a manual override both flow through here with no
  // re-analyze (the getter re-derives on each change-detection pass). A header not referenced by any
  // column-map field is absent from the map (rendered blank — no "Unmapped" clutter). fusedColumns take
  // precedence with their own combined-column label. Bound as a property read, not a method call.
  get mappedFieldByHeader(): Record<string, string> {
    const map: Record<string, string> = {};
    const columnMap = this.columnMap;
    if (!columnMap) return map;
    for (const field of this.columnMapFields) {
      const header = columnMap[field.key];
      if (header) map[header] = field.label;
    }
    for (const fused of columnMap.fusedColumns ?? []) {
      if (fused) map[fused] = this.fusedColumnLabel;
    }
    return map;
  }

  // 1-based index of the first row on the current page (0 when there are no rows).
  get previewRangeStart(): number {
    return this.previewTotal === 0 ? 0 : this.previewPageIndex * this.previewPageSize + 1;
  }

  get previewRangeEnd(): number {
    return Math.min((this.previewPageIndex + 1) * this.previewPageSize, this.previewTotal);
  }

  get canPreviewPrev(): boolean {
    return this.previewPageIndex > 0;
  }

  get canPreviewNext(): boolean {
    return (this.previewPageIndex + 1) * this.previewPageSize < this.previewTotal;
  }

  previewPrevPage(): void {
    if (this.canPreviewPrev) this.previewPageIndex--;
  }

  previewNextPage(): void {
    if (this.canPreviewNext) this.previewPageIndex++;
  }

  // ---- Step 3: review ----

  // processedCount is the number actually persisted (ingested) by THIS import; loadedCount is how many
  // rows were parsed from the file. They are distinct — a file can parse cleanly yet ingest nothing.
  get importFailedEntirely(): boolean {
    return !!this.response && this.response.processedCount === 0;
  }

  // Some rows ingested, but not all parsed rows made it (errors) — a partial success, not a clean one.
  get importPartial(): boolean {
    return !!this.response && this.response.processedCount > 0 && this.response.erroredCount > 0;
  }

  // Every attempted row ingested with no errors.
  get importSucceeded(): boolean {
    return !!this.response && this.response.processedCount > 0 && this.response.erroredCount === 0;
  }

  close(): void {
    // Signal the caller to refresh when anything was ingested.
    this.dialogRef.close((this.response?.processedCount ?? 0) > 0);
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  trackByIndex = trackByIndex;
}
